import { useContext, useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../../i18n/index.js';
import { formatDuration } from '../../arena/useArena.js';
import { playSound } from '../../audio/sounds.js';
import { useTurnTicks } from '../common/useTurnTicks.js';
import { GameOverBanner, GameOverContext } from '../common/TurnStatus.js';
import { Board, type BoardMarker } from './Board.js';
import { ShipSprite } from './ShipSprite.js';
import { turnClock } from '../../../shared/arena/turnClock.js';
import {
  FLEET_SIZES,
  sameCell,
  type BattleshipLastMove,
  type BattleshipView,
  type Cell,
} from '../../../shared/games/battleship/index.js';
import type { GameStateView } from '../../../shared/protocol.js';

/** Remaining ships per size, e.g. for "4 ●  3 ●●  2 ●○○  1 ●●○○". */
function FleetStatus(props: { sunkSizes: number[] }) {
  const sunk = [...props.sunkSizes];
  return (
    <div class="fleet-status" aria-label={t.battleship.shipsRemaining}>
      {FLEET_SIZES.map((size) => {
        const i = sunk.indexOf(size);
        const isSunk = i >= 0;
        if (isSunk) sunk.splice(i, 1);
        return (
          <span class={`fleet-chip size-${size} ${isSunk ? 'sunk' : ''}`}>
            <ShipSprite size={size} orientation="h" />
          </span>
        );
      })}
    </div>
  );
}

/** Match screen (suunnitelma.md sections 15–19). All timing comes from the server state. */
export function MatchView(props: {
  state: GameStateView<BattleshipView>;
  serverNow: number;
  onShoot: (cell: Cell) => Promise<string | null>;
}) {
  const { state, serverNow } = props;
  const view = state.module;
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const over = useContext(GameOverContext);
  const myTurn = state.turn?.userId === state.me.id;
  const current = myTurn ? state.me : state.opponent;
  const clock = state.turn
    ? turnClock({
        turnStartedAt: state.turn.startedAt,
        turnTimeMs: state.settings.turnTimeMs,
        timeBankMs: current.timeBankMs,
        now: serverNow,
      })
    : null;
  const turnFraction = clock ? clock.turnRemainingMs / state.settings.turnTimeMs : 0;

  const last = state.lastMove?.data as BattleshipLastMove | undefined;
  const lastByMe = state.lastMove?.byUserId === state.me.id;

  // Sound effects (suunnitelma.md section 32): shot + result, turn change, time bank warning.
  const lastKey = last ? `${state.lastMove!.byUserId}:${last.x},${last.y}` : '';
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current || !last) return;
    playSound('shot');
    playSound(last.result === 'miss' ? 'splash' : last.result, 260);
  }, [lastKey]);
  useEffect(() => {
    if (!firstRender.current && myTurn) playSound('turn', last ? 900 : 0);
  }, [state.turn?.startedAt]);
  useTurnTicks(clock, myTurn);
  useEffect(() => {
    firstRender.current = false;
  }, []);

  const targetMarkers: BoardMarker[] = view.target.shots.map((s) => ({
    ...s,
    latest: Boolean(last && lastByMe && sameCell(s, last)),
  }));
  const ownMarkers: BoardMarker[] = view.incoming.map((s) => ({
    ...s,
    latest: Boolean(last && !lastByMe && sameCell(s, last)),
  }));
  const fleet = view.fleet ?? [];

  const shoot = async (cell: Cell) => {
    if (!myTurn || pending) return;
    setPending(true);
    setError(await props.onShoot(cell));
    setPending(false);
  };

  return (
    <div class="match">
      {over ? (
        <GameOverBanner over={over} />
      ) : (
        <div class={`turn-banner ${myTurn ? 'mine' : 'theirs'}`} role="status" key={state.turn?.startedAt}>
          <div class="turn-text">
            {myTurn ? t.battleship.yourTurn : t.battleship.opponentTurn(state.opponent.displayName)}
            {last && (
              <span class={`last-shot shot-${last.result}`} key={`${last.x}-${last.y}`}>
                {t.battleship.lastShot[last.result]}
              </span>
            )}
          </div>
          {clock && clock.phase === 'turn' && (
            <div class="turn-bar" aria-hidden="true">
              <div class="turn-bar-fill" style={{ width: `${Math.max(0, Math.min(1, turnFraction)) * 100}%` }} />
            </div>
          )}
          {clock && clock.phase !== 'turn' && (
            <div class="time-bank">
              {myTurn ? t.battleship.timeBank : t.battleship.opponentTimeBank}: <strong>{formatDuration(clock.bankRemainingMs)}</strong>
            </div>
          )}
        </div>
      )}

      {!over && !state.opponent.online && <div class="conn-banner">{t.battleship.opponentOffline}</div>}
      {error && <p class="error">{error}</p>}

      <div class="boards">
        <Board
          title={t.battleship.targetBoard}
          class={`board-target ${myTurn && !pending ? 'active' : ''}`}
          ships={view.target.sunkShips.map((ship) => ({ ship, sunk: true }))}
          markers={targetMarkers}
          knownEmpty={view.knownEmpty}
          onCellClick={shoot}
          isCellDisabled={(cell) =>
            !myTurn ||
            pending ||
            view.target.shots.some((s) => sameCell(s, cell)) ||
            view.knownEmpty.some((c) => sameCell(c, cell))
          }
        >
          <FleetStatus sunkSizes={view.target.sunkShips.map((s) => s.size)} />
        </Board>
        <Board
          title={t.battleship.ownBoard}
          class="board-own"
          ships={fleet.map((ship, i) => ({ ship, sunk: view.sunkOwn.includes(i), class: 'own' }))}
          markers={ownMarkers}
        >
          <FleetStatus sunkSizes={view.sunkOwn.map((i) => fleet[i]!.size)} />
        </Board>
      </div>
    </div>
  );
}
